import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import { getAuth } from "firebase/auth";

const ACCEPTED_TYPES = [
  "image/*",
  "video/*",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];

Modal.setAppElement('#root');

const Attachments = ({ userId, projectId, projectName }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const auth = getAuth();
  const user = auth.currentUser;

const getSignedUrl = async (fileName) => {
  const token = await user.getIdToken();
  const res = await axios.get("https://modularobjects.com/checkout/api/upload/signed-url", {
    headers: { Authorization: `Bearer ${token}` },
    params: { projectId, revision: "R1", fileName }
  });

  const baseUrl = "https://modularobjects.com";  // Osnovni URL sajta
  let fileUrl = res.data.url;

  // Ako već postoji osnovni URL u 'res.data.url', nemoj ga ponovo dodavati
  if (!fileUrl.startsWith(baseUrl)) {
    fileUrl = `${baseUrl}${fileUrl}`;  // Dodajte osnovni URL samo ako nije prisutan
  }

  return fileUrl;
};



  useEffect(() => {
    const fetchExistingFiles = async () => {
      if (!projectId || !user) return;
      setLoading(true);
      try {
        const token = await user.getIdToken();
        const res = await axios.get("https://modularobjects.com/checkout/api/upload", {
          params: { projectId, revision: "R1" },
          headers: { Authorization: `Bearer ${token}` }
        });
        setUploaded(res.data.files || []);
      } catch (err) {
        console.error("Failed to fetch existing attachments", err);
      } finally {
        setLoading(false);
      }
    };

    fetchExistingFiles();
  }, [projectId, user]);

  const handleFileChange = (e) => {
    setAttachments(Array.from(e.target.files));
  };

  const handleUpload = async () => {
  if (!attachments.length || !user || !projectId) return;
  setLoading(true);
  try {
    const token = await user.getIdToken();
    const formData = new FormData();
    formData.append("projectId", projectId);
    formData.append("userId", user.uid);  // Proverite da li je userId ispravno dodato
    formData.append("revision", "R1");

    attachments.forEach(file => formData.append("attachments", file));

    // Logujte formData pre slanja
    console.log("FormData being sent:", formData);

    const res = await axios.post("https://modularobjects.com/checkout/api/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`
      },
      params: { projectId, revision: "R1" }
    });

    setUploaded(prev => [...prev, ...(res.data.files || [])]);
    setAttachments([]);
    alert("Files uploaded successfully!");
  } catch (err) {
    console.error("Upload failed:", err);
    alert("Upload failed. Please try again.");
  } finally {
    setLoading(false);
  }
};


  const handleDelete = async (fileName) => {
    if (!window.confirm("Are you sure you want to delete this file?") || !user || !projectId) return;

    try {
      const token = await user.getIdToken();
      await axios.delete("https://modularobjects.com/checkout/api/upload", {
        data: { fileName, projectId, revision: "R1" },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploaded(prev => prev.filter(f => f.name !== fileName));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete file");
    }
  };

const openPreview = async (file) => {
  const url = await getSignedUrl(file.name); // Dobijaš URL za fajl
  setPreviewFile({ ...file, url }); // Postavi preview fajl sa URL-om
};

const closePreview = () => setPreviewFile(null); // Zatvori preview modal

if (!user) {
  return <div className="p-4 text-red-500">Please log in to access attachments</div>;
}

return (
  <div className="space-y-6 relative">
    <label className="block font-bold text-lg text-primary-dark">
      📌 Attachments for: {projectName || "Untitled Project"}
    </label>

    <input
      type="file"
      name="attachments"
      multiple
      accept={ACCEPTED_TYPES.join(",")}
      onChange={handleFileChange}
      className="w-full border-2 border-dashed border-gray-300 dark:border-neutral-700 rounded p-4 bg-white dark:bg-neutral-900 text-sm"
      disabled={loading}
    />

    {attachments.length > 0 && (
      <div className="mt-4">
        <h4 className="font-semibold">Selected Files:</h4>
        <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-300">
          {attachments.map((file, i) => (
            <li key={i}>{file.name}</li>
          ))}
        </ul>
        <button
          onClick={handleUpload}
          className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded"
          disabled={loading}
        >
          {loading ? 'Uploading...' : 'Upload Files'}
        </button>
      </div>
    )}

   {uploaded.length > 0 && (
  <div className="mt-6">
    <h3 className="font-semibold text-lg mb-4">Uploaded Files:</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {uploaded.map((file, i) => {
        const isImage = file.type?.startsWith("image/");
        const isVideo = file.type?.startsWith("video/");
        const isPdf = file.type === "application/pdf";
        const isWord = file.type?.includes("word");
        const isExcel = file.type?.includes("excel");

        // Generisanje ikona za različite tipove fajlova
        let fileIcon;
        if (isImage) fileIcon = "🖼️";
        else if (isVideo) fileIcon = "🎥";
        else if (isPdf) fileIcon = "📄";
        else if (isWord) fileIcon = "📃";
        else if (isExcel) fileIcon = "📊";
        else fileIcon = "📁"; // Default icon

       return (
  <div key={i} className="p-4 bg-white dark:bg-neutral-800 rounded shadow relative">
    {/* Preview button */}
    <div onClick={() => openPreview(file)} className="cursor-pointer mt-2">
      <div className="flex items-center justify-center h-32 bg-gray-100 dark:bg-neutral-700 rounded text-gray-600 dark:text-gray-300 text-sm">
        {/* File type and file name */}
        <div className="flex items-center justify-center flex-col text-center mb-4">
          <div className="text-3xl">{fileIcon}</div> {/* Icon for file type */}
          <p className="font-medium">{file.name}</p> {/* File name */}
        </div>
      </div>
    </div>

    {/* Buttons container with flexbox to align Delete button with Download button */}
    <div className="flex justify-between items-center mt-2">
      {/* Download button */}
      <a
        href="#"
        onClick={async (e) => {
          e.preventDefault();
          const signedUrl = await getSignedUrl(file.name);

          // Create a dynamic <a> tag with the download attribute
          const link = document.createElement("a");
          link.href = signedUrl;
          link.download = file.name; // Set the filename for download

          // Trigger the download by simulating a click event
          document.body.appendChild(link); // Append the link to the body
          link.click(); // Simulate click on the link to trigger the download
          document.body.removeChild(link); // Remove the link from the DOM after the download starts
        }}
        className="text-blue-600 hover:underline text-sm"
      >
        ⬇️ Download
      </a>

      {/* Delete button moved to the bottom-right, aligned with Download */}
      <button
        onClick={() => handleDelete(file.name)}
        className="text-red-500 hover:text-red-700 text-sm"
        title="Delete file"
        disabled={loading}
      >
        ✕ Delete
      </button>
    </div>
  </div>
);



      })}
    </div>
  </div>
)}



    {loading && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-2">
          <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full" />
          <span>Processing files...</span>
        </div>
      </div>
    )}

    {/* Modal za preview fajlova */}
    <Modal
      isOpen={!!previewFile}
      onRequestClose={closePreview}
      contentLabel="File Preview"
      className="fixed inset-0 p-8 bg-white dark:bg-neutral-900 z-50 flex justify-center items-center"
      overlayClassName="fixed inset-0 bg-black bg-opacity-80"
    >
      {previewFile && (
        <div className="max-w-4xl w-full bg-white p-4 rounded shadow-lg relative">
          <button
            onClick={closePreview}
            className="absolute top-2 right-2 text-red-600 text-xl font-bold"
          >
            ✕
          </button>
          <h2 className="text-lg font-bold mb-4">{previewFile.name}</h2>

          {/* Prikaz za slike */}
          {previewFile.type?.startsWith("image/") && (
            <img src={previewFile.url} alt={previewFile.name} className="w-full max-h-[80vh] object-contain" />
          )}

          {/* Prikaz za PDF */}
          {previewFile.type === "application/pdf" && (
            <iframe
              src={previewFile.url}
              title={previewFile.name}
              className="w-full h-[80vh] border rounded"
            />
          )}

          {/* Prikaz za video */}
          {previewFile.type?.startsWith("video/") && (
            <video controls className="w-full max-h-[80vh]">
              <source src={previewFile.url} type={previewFile.type} />
            </video>
          )}
        </div>
      )}
    </Modal>
  </div>
);

};

export default Attachments;
